import SettingsIcon from '@/assets/builder/icon/Settings'
import { Button } from '@/components/ui/button'
import { AddIcon } from '@/assets/builder/icon/AddIcon'
import { Eye, Loader2, Rocket } from 'lucide-react'

const SettingsButton = ({ disabled }: { disabled?: boolean }) => {
    return (
        <div
            className={`rounded-lg h-8 pt-[1px] pb-[3px] px-[1px] transition-all ${disabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-black hover:bg-opacity-80 cursor-pointer'}`}
        >
            <Button
                disabled={disabled}
                className={`h-full text-foreground text-[13px] gap-1 font-[650] transition-all ${
                    disabled
                        ? 'bg-gray-200 cursor-not-allowed text-foreground'
                        : 'bg-[#CFFF6D] hover:bg-[#DAFF8C] hover:translate-y-[1px]'
                }`}
            >
                <SettingsIcon />
                Continue to Settings
            </Button>
        </div>
    )
}
const PublishButton = ({ disabled }: { disabled?: boolean }) => {
    return (
        <div
            className={`rounded-lg h-8 pt-[1px] pb-[3px] px-[1px] transition-all ${disabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-black hover:bg-opacity-80 cursor-pointer'}`}
        >
            <Button
                disabled={disabled}
                className={`h-full text-foreground text-[13px] gap-1 font-[650] transition-all ${
                    disabled
                        ? 'bg-gray-200 cursor-not-allowed text-foreground'
                        : 'bg-[#CFFF6D] hover:bg-[#DAFF8C] hover:translate-y-[1px]'
                }`}
            >
                <Rocket size={16} />
                Publish
            </Button>
        </div>
    )
}
const AddQuestion = ({
    disabled,
    isLoading,
}: {
    disabled?: boolean
    isLoading?: boolean
}) => {
    return (
        <div
            className={`rounded-lg h-7 w-7 pt-[1px] pb-[2px] px-[1px] transition-all ${disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-black hover:bg-opacity-80 cursor-pointer'}`}
        >
            <Button
                disabled={disabled}
                className={`h-full w-full text-foreground p-0 font-[650] transition-all shadow-sm shadow-gray-600 ${
                    disabled
                        ? 'bg-gray-200 cursor-not-allowed text-background'
                        : 'bg-black '
                }`}
            >
                <div className="">
                    {isLoading ? (
                        <Loader2
                            className="h-4 w-4 animate-spin"
                            color="white"
                        />
                    ) : (
                        <AddIcon />
                    )}
                </div>
            </Button>
        </div>
    )
}
const PremiumButton = ({ disabled }: { disabled?: boolean }) => {
    return (
        <div
            className={`rounded-lg h-7 w-12 pt-[1px] pb-[2px] px-[1px] transition-all ${disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-black hover:bg-opacity-80 cursor-pointer'}`}
        >
            <Button
                disabled={disabled}
                className={`h-full w-full text-foreground p-0 font-[650] transition-all shadow-sm shadow-gray-600 ${
                    disabled
                        ? 'bg-gray-200 cursor-not-allowed text-background'
                        : 'bg-black '
                }`}
            >
                <div className="flex items-center">
                    <AddIcon />
                    <img
                        src={'/utils/crown.png'}
                        alt="crown"
                        className="w-4 h-4"
                        loading="lazy"
                    />
                </div>
            </Button>
        </div>
    )
}

const CreateSurveyButton = ({
    text,
    disabled,
    onClick,
    icon,
}: {
    text: string
    disabled?: boolean
    onClick?: () => void
    icon?: React.ReactNode
}) => {
    return (
        <div
            className={`w-fit rounded-lg h-8 pt-[1px] pb-[3px] px-[1px] transition-all ${disabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-black hover:bg-opacity-80 cursor-pointer'}`}
        >
            <Button
                disabled={disabled}
                className={`h-full text-foreground text-[13px] gap-1 font-[650] transition-all ${
                    disabled
                        ? 'bg-gray-200 cursor-not-allowed text-foreground'
                        : 'bg-[#CFFF6D] hover:bg-[#DAFF8C] hover:translate-y-[1px]'
                }`}
                onClick={onClick}
            >
                {icon}
                {text}
            </Button>
        </div>
    )
}
const PreviewTemplateButton = ({ disabled }: { disabled?: boolean }) => {
    return (
        <div
            className={`rounded-lg h-8 pt-[1px] pb-[2px] px-[1px] transition-all ${disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-[#B5B5B5] hover:bg-opacity-80 cursor-pointer'}`}
        >
            <Button
                disabled={disabled}
                className={`h-full text-foreground text-[12px] gap-1 font-[550] transition-all ${
                    disabled
                        ? 'bg-gray-100 cursor-not-allowed'
                        : 'bg-white hover:bg-gray-50 hover:translate-y-[1px]'
                }`}
            >
                <Eye />
                Preview
            </Button>
        </div>
    )
}
const SecondaryButton = ({
    icon,
    rightIcon,
    text,
    disabled,
    onClick,
}: {
    icon?: React.ReactNode
    rightIcon?: React.ReactNode
    text: string
    disabled?: boolean
    onClick?: () => void
}) => {
    return (
        <div
            className={`w-fit rounded-lg h-8 pt-[1px] pb-[2px] px-[1px] transition-all ${disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-[#B5B5B5] hover:bg-opacity-80 cursor-pointer'}`}
        >
            <Button
                disabled={disabled}
                className={`h-full text-foreground text-[12px] gap-1 font-[550] transition-all ${
                    disabled
                        ? 'bg-gray-100 cursor-not-allowed'
                        : 'bg-white hover:bg-gray-50 hover:translate-y-[1px]'
                }`}
                onClick={onClick}
            >
                {icon}
                {text}
                {rightIcon}
            </Button>
        </div>
    )
}
const TertiaryButton = ({
    icon,
    text,
    disabled,
    onClick,
}: {
    icon?: React.ReactNode
    text: string
    disabled?: boolean
    onClick?: () => void
}) => {
    return (
        <div
            className={`rounded-lg h-fit w-fit pt-[1px] pb-[2px] px-[1px] transition-all ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <Button
                disabled={disabled}
                className={`h-[28px] w-full text-white text-[12px] px-[12px] py-[6px] font-[650] transition-all shadow-sm shadow-gray-600 border border-[#656565] gap-1 ${
                    disabled
                        ? 'disabled:bg-gray-700'
                        : 'bg-[#3f3f3f] hover:translate-y-[1px] hover:bg-[#3f3f3f]/80'
                }`}
                onClick={onClick}
            >
                {icon}
                {text}
            </Button>
        </div>
    )
}
export {
    SettingsButton,
    AddQuestion,
    PremiumButton,
    PublishButton,
    CreateSurveyButton,
    PreviewTemplateButton,
    SecondaryButton,
    TertiaryButton,
}
